import { useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import { Separator } from '@staff-app/ui';
import { useAuthStore } from '@/stores/auth-store';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import { AgentTimeTable } from '../components/AgentTimeTable';
import { MonthSelect } from '../components/MonthSelect';
import { ProjectCategoriesSection } from '../components/ProjectCategoriesSection';
import { useOpticlockAgentTotals } from '../hooks/useOpticlockDashboard';
import { currentMonthCentral } from '../lib/month';

/**
 * Time Dashboard — root's monthly rollup plus the project-category admin.
 *
 * The route already carries `<AdminRoleRoute requiredRole="root" />`; the client-side re-check
 * below is the house belt-and-braces pattern (HardwareProductsPage / AdminUsersPage do the
 * same). Neither is the real gate — every query here is `@RequireRoles(ROOT)` server-side.
 */
export function TimeDashboardPage() {
  const me = useAuthStore((s) => s.adminUser);
  const isRoot = !!me?.roles?.includes(ADMIN_ROLES.ROOT);

  const [month, setMonth] = useState(currentMonthCentral());
  const { data, loading, error } = useOpticlockAgentTotals(month);

  if (!isRoot) return <PendingApprovalPage />;

  const rows = data?.opticlockAgentMonthTotals ?? [];

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="opticlock-dashboard-page">
      <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
        <BarChart3 className="h-6 w-6" />
        {'Time Dashboard'}
      </h1>
      <p className="max-w-3xl text-xs text-muted-foreground">
        {
          'Where the support team’s non-ticket hours went. An agent who logged nothing this month is not listed — a missing row means no entries, not zero hours.'
        }
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <MonthSelect value={month} onChange={setMonth} testId="opticlock-dashboard-month" />
      </div>

      {error ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="opticlock-dashboard-error"
        >
          {error.message}
        </p>
      ) : null}

      <AgentTimeTable rows={rows} loading={loading} />

      <Separator className="my-2" />

      <ProjectCategoriesSection />
    </div>
  );
}
